import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  MapPin, ShieldCheck, Car, Camera, Gauge, FileText, CheckCircle2, AlertTriangle,
  Phone, ArrowRight, CalendarClock, Search, Wrench, Droplets, Zap, UserCheck, Sparkles,
  ClipboardCheck, ChevronRight, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';
import BrandHero from '../components/BrandHero';
import { LOCATIONS, getLocation } from '../data/locations';
import { SERVICES } from '../data/services';

const SERVICE_ICONS = { ShieldCheck, UserCheck, Search, Car, Sparkles, ClipboardCheck };

const WHY_CARLENS = [
  { icon: ShieldCheck, title: 'Truly Independent', text: 'No dealer affiliation of any kind. Our only job is to tell you the truth about the vehicle.' },
  { icon: Car, title: 'We Come to the Car', text: 'Fully mobile service — we meet the seller wherever the vehicle is located.' },
  { icon: Gauge, title: 'OBD-II Diagnostic Scan', text: 'We read the vehicle’s computer for stored and pending trouble codes a test drive can miss.' },
  { icon: Camera, title: 'Photo Evidence', text: 'Detailed photos of every key finding, so you see exactly what we see.' },
  { icon: FileText, title: 'Vehicle Decision Report', text: 'A clear, professional report with a buy / negotiate / walk-away recommendation.' },
  { icon: CheckCircle2, title: 'Buyer-First, Always', text: 'Inspect. Inform. Protect. We work for you — never the seller or a dealership.' },
];

const PROBLEMS = [
  { icon: AlertTriangle, t: 'Hidden accident repairs', d: 'Repainted panels and filler work that hide prior collision damage.' },
  { icon: Droplets, t: 'Flood & water damage', d: 'Corrosion, mud lines, and electrical gremlins from water exposure.' },
  { icon: Wrench, t: 'Frame damage', d: 'Structural damage or corrosion that affects safety and value.' },
  { icon: Gauge, t: 'Transmission concerns', d: 'Rough shifting, slipping, or stored codes pointing to costly repairs.' },
  { icon: Car, t: 'Engine leaks', d: 'Oil, coolant, and seal leaks that signal expensive work ahead.' },
  { icon: Wrench, t: 'Suspension wear', d: 'Worn components that hurt ride quality, safety, and tire life.' },
  { icon: Zap, t: 'Electrical issues', d: 'Intermittent faults and failing modules that are pricey to chase.' },
  { icon: AlertTriangle, t: 'Dashboard warning lights', d: 'Cleared or masked warning lights that reappear after purchase.' },
  { icon: Gauge, t: 'Tire problems', d: 'Uneven wear revealing alignment, suspension, or frame issues.' },
  { icon: ClipboardCheck, t: 'Poor maintenance', d: 'Neglected service history that shortens the vehicle’s life.' },
];

const PROCESS = [
  { n: '01', t: 'Schedule', d: 'Book online in minutes and tell us where the vehicle is located.' },
  { n: '02', t: 'We Travel to the Vehicle', d: 'Our inspector meets the seller on-site — you don’t have to be there.' },
  { n: '03', t: 'Complete Inspection', d: 'A thorough evaluation plus an OBD-II scan and detailed photos.' },
  { n: '04', t: 'Deliver the Report', d: 'You receive your CarLens Vehicle Decision Report electronically.' },
  { n: '05', t: 'You Decide with Confidence', d: 'Buy, negotiate, or walk away — now backed by facts.' },
];

const RESOURCES = [
  { label: 'Used Car Buying Guide', to: '/academy/ultimate-guide-to-buying-a-used-car' },
  { label: 'Vehicle History & Flood Guide', to: '/academy/category/vehicle-history' },
  { label: 'Facebook Marketplace Buying Tips', to: '/academy/category/marketplace-tips' },
  { label: 'Dealer vs Private Seller', to: '/academy/dealer-vs-private-seller' },
  { label: 'Questions to Ask Before Buying', to: '/academy/questions-to-ask-before-buying' },
  { label: 'CarLens Academy', to: '/academy' },
];

const CtaBand = ({ city, variant = 'solid' }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`rounded-3xl p-8 sm:p-10 text-center ${
        variant === 'solid'
          ? 'bg-gradient-to-br from-orange-500 to-orange-600'
          : 'border border-orange-500/40 bg-gradient-to-br from-[#1c1c1f] to-[#141416]'
      }`}
      data-testid="location-cta-band"
    >
      <h3 className={`text-2xl sm:text-3xl font-extrabold ${variant === 'solid' ? 'text-white' : 'text-white'}`} style={{ fontFamily: 'Space Grotesk' }}>
        Buying a used vehicle in {city}?
      </h3>
      <p className={`mt-2 ${variant === 'solid' ? 'text-orange-50' : 'text-zinc-300'}`}>
        Know the truth before you buy. Book your independent CarLens inspection today.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={() => navigate('/book')}
          className={`rounded-full px-8 py-5 h-auto text-base font-semibold ${
            variant === 'solid' ? 'bg-white text-orange-600 hover:bg-orange-50' : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
          }`}
          data-testid="location-book-btn"
        >
          Book Your Inspection <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <a href="tel:+13213003256" className={`inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 font-semibold transition-colors ${
          variant === 'solid' ? 'bg-orange-600/40 text-white hover:bg-orange-600/60' : 'border border-zinc-600 text-white hover:bg-white/5'
        }`} data-testid="location-call-btn">
          <Phone className="w-4 h-4" /> 321.300.3256
        </a>
      </div>
    </div>
  );
};

const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const city = getLocation(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
        <div>
          <h1 className="text-2xl font-bold text-white mb-3">Service area not found</h1>
          <Link to="/locations" className="text-orange-400 hover:text-orange-300">View all CarLens service areas</Link>
        </div>
      </div>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://carlensauto.com';
  const path = `/locations/${city.slug}`;
  const nearbyCities = city.nearby.map(getLocation).filter(Boolean);

  const isGA = (city.county || '').includes('GA');
  const regionLabel = isGA ? 'Southeast Georgia' : 'Northeast Florida';
  const cityLabel = (c) => ((c.county || '').includes('GA') ? c.name : `${c.name}, Florida`);
  const h1Label = isGA ? city.name : `${city.name}, Florida`;

  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'AutomotiveBusiness',
    name: 'CarLens Inspections',
    description: `Independent mobile pre-purchase vehicle inspections serving ${city.name} and the surrounding ${regionLabel} area.`,
    slogan: 'Know the Truth Before You Buy.',
    url: `${origin}${path}`,
    telephone: '+13213003256',
    email: 'carlensauto@gmail.com',
    priceRange: '$$',
    areaServed: [
      { '@type': 'City', name: cityLabel(city) },
      ...nearbyCities.map((n) => ({ '@type': 'City', name: cityLabel(n) })),
    ],
    address: { '@type': 'PostalAddress', addressRegion: isGA ? 'GA' : 'FL', addressCountry: 'US' },
    knowsAbout: ['Pre-purchase vehicle inspection', 'Used car inspection', 'Mobile vehicle inspection'],
  };

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Service Areas', path: '/locations' },
    { name: city.name, path },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        siteName="CarLens"
        title={city.seoTitle}
        description={city.metaDescription}
        path={path}
        breadcrumbs={breadcrumbs}
        faq={city.faqs}
        extraSchemas={[localBusiness]}
      />

      <BrandHero />

      <div className="max-w-4xl mx-auto px-5 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6" data-testid="location-breadcrumb">
          <Link to="/" className="hover:text-zinc-300">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/locations" className="hover:text-zinc-300">Service Areas</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-300">{city.name}</span>
        </nav>

        {/* Hero */}
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-orange-300 mb-5">
          <MapPin className="w-3.5 h-3.5" /> {city.county} · {regionLabel}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontFamily: 'Space Grotesk' }} data-testid="location-h1">
          Pre-Purchase Vehicle Inspections in {h1Label}
        </h1>
        <p className="mt-6 text-lg leading-8 text-zinc-300">{city.heroLead}</p>
        <div className="mt-8"><CtaBand city={city.name} variant="solid" /></div>

        {/* About buying used in city */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk' }}>
            Buying a Used Car in {city.name}
          </h2>
          <div className="space-y-4 text-[15px] leading-8 text-zinc-300">
            {city.localParas.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </section>

        {/* Neighborhoods & corridors we cover */}
        {city.neighborhoods && city.neighborhoods.length > 0 && (
          <section className="mt-16" data-testid="location-neighborhoods">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              Neighborhoods &amp; Corridors We Cover in {city.name}
            </h2>
            <p className="text-zinc-400 mb-6 leading-7">
              CarLens is fully mobile, so we meet the seller and inspect the vehicle right where it is — across every {city.name} neighborhood and along the main routes buyers travel to view cars:
            </p>
            <div className="flex flex-wrap gap-2.5">
              {city.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-[#161618] px-3.5 py-1.5 text-sm text-zinc-300"
                  data-testid={`location-neighborhood-${n}`}
                >
                  <MapPin className="w-3.5 h-3.5 text-orange-400" /> {n}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Why CarLens */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            Why Choose CarLens in {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {WHY_CARLENS.map((w) => (
              <div key={w.title} className="rounded-2xl border border-zinc-800 bg-[#161618] p-5">
                <w.icon className="w-6 h-6 text-orange-400 mb-2.5" />
                <h3 className="font-bold text-white">{w.title}</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-6">{w.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Services */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Inspection Services Available in {city.name}
          </h2>
          <p className="text-zinc-400 mb-6">Every service is fully mobile — we come to the vehicle anywhere in the {city.name} area.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {SERVICES.map((s) => {
              const Icon = SERVICE_ICONS[s.icon] || ShieldCheck;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group rounded-2xl border border-zinc-800 bg-[#161618] p-5 hover:border-orange-500/50 transition-colors" data-testid={`location-service-${s.slug}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-orange-400" />
                    <h3 className="font-bold text-white group-hover:text-orange-300 transition-colors">{s.name}</h3>
                  </div>
                  <p className="text-sm text-zinc-400 leading-6">{s.tagline}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-400">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Common problems */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            Common Problems We Find on {city.name} Vehicles
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {PROBLEMS.map((p) => (
              <div key={p.t} className="flex gap-3 rounded-xl border border-zinc-800 bg-[#161618] p-4">
                <p.icon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white text-[15px]">{p.t}</h3>
                  <p className="text-sm text-zinc-400 leading-6">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>The CarLens Inspection Process</h2>
          <div className="space-y-3">
            {PROCESS.map((step) => (
              <div key={step.n} className="flex gap-4 rounded-2xl border border-zinc-800 bg-[#161618] p-5">
                <span className="text-2xl font-extrabold text-orange-500 shrink-0" style={{ fontFamily: 'Space Grotesk' }}>{step.n}</span>
                <div>
                  <h3 className="font-bold text-white">{step.t}</h3>
                  <p className="text-sm text-zinc-400 mt-0.5 leading-6">{step.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why local buyers trust */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Why {city.name} Buyers Trust CarLens</h2>
          <p className="text-[15px] leading-8 text-zinc-300">{city.trustPara}</p>
          <p className="mt-4 text-[15px] leading-8 text-zinc-300">
            A vehicle purchase is one of the biggest financial decisions most people make. As an independent inspector with no dealer ties, CarLens exists for one reason: to give {city.name} buyers the honest, complete picture they need before they commit. That’s our promise — <span className="text-white font-semibold">Inspect. Inform. Protect.</span>
          </p>
          <div className="mt-8"><CtaBand city={city.name} variant="outline" /></div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            {city.name} Vehicle Inspection FAQs
          </h2>
          <div className="space-y-3" data-testid="location-faqs">
            {city.faqs.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] p-5" data-testid={`location-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-orange-400 shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-zinc-400 leading-7">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Nearby areas */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk' }}>Nearby Areas We Serve</h2>
          <div className="flex flex-wrap gap-3">
            {nearbyCities.map((n) => (
              <Link key={n.slug} to={`/locations/${n.slug}`} className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-[#161618] px-5 py-2.5 text-sm text-zinc-200 hover:border-orange-500/50 hover:text-orange-300 transition-colors" data-testid={`location-nearby-${n.slug}`}>
                <MapPin className="w-3.5 h-3.5 text-orange-400" /> {n.name}
              </Link>
            ))}
            <Link to="/locations" className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-5 py-2.5 text-sm text-orange-300 hover:bg-orange-500/20 transition-colors">
              All Service Areas <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Resources */}
        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk' }}>Helpful Resources</h2>
          <Link
            to={`/academy/buying-a-used-car-in-${city.slug}`}
            className="flex items-center justify-between rounded-2xl border border-orange-500/40 bg-gradient-to-br from-orange-500/15 to-orange-600/5 px-6 py-5 mb-3 hover:border-orange-500/70 transition-colors"
            data-testid="location-local-guide-link"
          >
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-orange-400 shrink-0" />
              <div>
                <span className="block text-white font-bold">Read: Buying a Used Car in {city.name}</span>
                <span className="block text-sm text-zinc-400 mt-0.5">Our local buyer’s guide — market insight, neighborhoods and what we find here.</span>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-orange-400 shrink-0" />
          </Link>
          <div className="grid sm:grid-cols-2 gap-3">
            {RESOURCES.map((r) => (
              <Link key={r.label} to={r.to} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-[#161618] px-5 py-4 text-zinc-200 hover:border-orange-500/50 hover:text-orange-300 transition-colors" data-testid={`location-resource-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>
                <span className="text-[15px] font-medium">{r.label}</span>
                <ArrowRight className="w-4 h-4 text-orange-400" />
              </Link>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-16">
          <CtaBand city={city.name} variant="solid" />
          <p className="mt-6 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <CalendarClock className="w-4 h-4" /> Same-day and next-day inspections often available in {city.name}.
          </p>
        </section>
      </div>
    </div>
  );
};

export default LocationPage;
